/** Welcome onboarding page that marks the tour seen and hosts step cards. */
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, Paper, Typography } from '@mui/material';
import { useT } from '@/i18n/useT';
import { useAuth } from '@/features/auth/useAuth';
import { markHintSeen } from '@/services/userService';
import { ONBOARDING_HINT_ONBOARDING } from '@/constants/onboardingHints';
import OnboardingWelcomeSteps from '@/features/onboarding/components/OnboardingWelcomeSteps';
import { resolveOnboardingWelcomeSkipPath } from '@/features/onboarding/utils/onboardingWelcomeReturnPath';

/** Survives React StrictMode remount without duplicate Firestore writes. */
const onboardingMarkedForUser = new Set();

/** Card slightly wider than the previous 480px step container. */
const WELCOME_CARD_MAX_WIDTH = 560;
/**
 * Keeps card height stable across welcome steps.
 * Measured (Playwright, post plusHint visibility:hidden): tallest stack is
 * subtaskEditing at 258px (1440 & 390); +12px margin → 270.
 */
export const WELCOME_STEPS_MIN_HEIGHT = 270;

const WelcomePage = () => {
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    const uid = user?.uid;
    if (!uid || onboardingMarkedForUser.has(uid)) {
      return;
    }
    onboardingMarkedForUser.add(uid);
    markHintSeen(uid, ONBOARDING_HINT_ONBOARDING).catch(() => {});

    // Clear from Set on unmount to allow re-marking on replay
    return () => {
      if (uid) {
        onboardingMarkedForUser.delete(uid);
      }
    };
  }, [user?.uid]);

  const handleSkip = () => {
    navigate(resolveOnboardingWelcomeSkipPath(location.state?.from), {
      replace: true,
    });
  };

  return (
    <Box
      data-testid="onboarding-welcome"
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: 4,
      }}
    >
      <Paper
        elevation={2}
        data-testid="onboarding-welcome-card"
        sx={{
          width: '100%',
          maxWidth: WELCOME_CARD_MAX_WIDTH,
          p: { xs: 2.5, sm: 4 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: 2,
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: -1 }}>
          <Button
            variant="text"
            size="small"
            onClick={handleSkip}
            sx={{
              color: 'text.secondary',
              fontSize: '0.8125rem',
              fontWeight: 400,
              textTransform: 'none',
              minWidth: 0,
              px: 0.5,
              py: 0,
            }}
          >
            {t('onboarding.tour.skip', 'Intro overslaan')}
          </Button>
        </Box>
        <Typography
          component="p"
          variant="subtitle2"
          color="text.secondary"
          textAlign="center"
        >
          {t('onboarding.tour.title', 'Van losse gedachte naar plan, in een minuut.')}
        </Typography>
        <Box sx={{ width: '100%', minHeight: WELCOME_STEPS_MIN_HEIGHT }}>
          <OnboardingWelcomeSteps />
        </Box>
      </Paper>
    </Box>
  );
};

export default WelcomePage;
